import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';
import { useCurrentUser } from '@bonusly/who-am-i';

import { Award, AwardDetail, AwardState, AwardVisibility, BudgetPeriod } from '../schemas/awards';
import { GroupType } from '../schemas/groups';
import { ensureID, Metadata, PaginatedData, PaginationParams, RecordOrID } from './meta';

/*
 * Shared utilities
 */

enum QueryKey {
	Awards = 'awards:awards',
	UserGiveableAwards = 'awards',
}

enum AwardType {
	Manual = 'manual',
}

const headers = {
	'Key-Inflection': 'camel',
};

/*
 * Path and query key builders
 */

export const awardsQueryKey = (params?: Record<string, any>) => [QueryKey.Awards, params].filter(Boolean);
export const awardQueryKey = (id: string) => [QueryKey.Awards, id];

export const awardsPath = (prefix = '') => `${prefix}/admin/awards/awards`;
export const awardPath = (id: string, prefix = '') => `${prefix}/admin/awards/awards/${id}`;

type AwardAPIData = {
	id: string;
	amount: number;
	budgetUsed: number | null;
	conditionsOperator: string | null;
	conditions: { attr: string; value: string }[] | null;
	giverId: string;
	hideGiver: boolean;
	iconUrl: string;
	message: string;
	name: string;
	state: string;
	periodType: string | null;
	visibility: string;
	budgetBoost: number;
	reasonEditable: boolean;
	pendingApprovals: number;
};

/*
 * Index queries
 */

type AwardsIndexParams = PaginationParams<{
	state?: 'active' | 'inactive';
}>;

export const useAwards = (params?: AwardsIndexParams, options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: awardsQueryKey(params),
		queryFn: async () => {
			const res = await http.get<PaginatedData<AwardAPIData>>(awardsPath(), {
				params,
				headers,
			});

			return [res.data.records.map((record) => Award.parse(record)), new Metadata(res.data.metadata)] as const;
		},
		enabled: options?.enabled,
	});
};

/*
 * Infinite index queries (for lazy-loaded, scroll-paginated lists)
 */

type InfiniteAwardsPage = { records: Award[]; metadata: Metadata };

export const infiniteAwardsQueryKey = (params?: Record<string, any>) => [QueryKey.Awards, 'infinite', params].filter(Boolean);

export const useInfiniteAwards = (params?: AwardsIndexParams, options?: { enabled?: boolean }) => {
	const http = useHttpClient();

	const query = useInfiniteQuery<InfiniteAwardsPage>({
		queryKey: infiniteAwardsQueryKey(params),
		queryFn: async ({ pageParam = 1 }) => {
			const res = await http.get<PaginatedData<AwardAPIData>>(awardsPath(), {
				params: { ...params, page_number: pageParam },
				headers,
			});

			return {
				records: res.data.records.map((record) => Award.parse(record)),
				metadata: new Metadata(res.data.metadata),
			};
		},
		getNextPageParam: (lastPage) => lastPage.metadata.pageNext ?? undefined,
		enabled: options?.enabled ?? true,
	});

	const allRecords = useMemo(() => query.data?.pages.flatMap((page) => page.records) ?? [], [query.data]);

	return { ...query, allRecords };
};

/*
 * Detail queries
 */

const createAwardFetcher = (http: HttpClient, id: string) => async () => {
	const res = await http.get<AwardAPIData>(awardPath(id), { headers });
	return AwardDetail.parse(res.data);
};

export const useAward = (id: string) => {
	return useQuery({
		queryKey: awardQueryKey(id),
		queryFn: createAwardFetcher(useHttpClient(), id),
	});
};

export const loadAward = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: awardQueryKey(id),
		queryFn: createAwardFetcher(http, id),
	});
};

/*
 * Update mutations
 */

type ApprovalProcessLevel = {
	position: number;
	allowedApproverIds: string[];
};

export type MutateAwardAPIData = {
	amount: number | null;
	amountEditable: boolean;
	approvalProcessLevels: ApprovalProcessLevel[] | null;
	budgetPerPeriod?: number | null;
	conditions: { attr: string; value: string }[] | null;
	conditionsOperator: GroupType | null;
	description: string;
	hideGiver: boolean;
	icon?: File | null;
	message: string | null;
	messageEditable: boolean;
	name: string;
	periodType: BudgetPeriod;
	state?: AwardState;
	title: string | null;
	visibility: AwardVisibility;
	visibilityEditable: boolean;
};

type MutateAwardOptions = {
	onError?: (error: unknown) => void;
	onMutate?: () => void;
	onSuccess?: (award?: Award) => void;
};

export const useDeactivateAward = (recordOrId: RecordOrID, options: MutateAwardOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAward();
	const invalidate = useInvalidateAwards();

	return useMutation({
		mutationFn: async () => {
			try {
				options.onMutate?.();

				const res = await http.patch(awardPath(ensureID(recordOrId)), { state: 'inactive' }, { headers });
				const record = Award.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useUpdateAward = (recordOrId: RecordOrID, options: MutateAwardOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAward();
	const invalidate = useInvalidateAwards();

	return useMutation({
		mutationFn: async (data: MutateAwardAPIData | FormData) => {
			try {
				options.onMutate?.();

				const res = await http.patch(awardPath(ensureID(recordOrId)), data, { headers });
				const record = Award.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Delete mutations
 */

export const useDeleteAward = (recordOrId: RecordOrID, options: MutateAwardOptions = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateAwards();

	return useMutation({
		mutationFn: async () => {
			try {
				await http.delete(awardPath(ensureID(recordOrId)), { headers });
				invalidate();
				options.onSuccess?.();
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Create mutations
 */

export const useCreateAward = (options: MutateAwardOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreAward();
	const invalidate = useInvalidateAwards();

	return useMutation({
		mutationFn: async (data: MutateAwardAPIData | FormData) => {
			options.onMutate?.();

			try {
				const res = await http.post(awardsPath(), data, { headers });
				const record = AwardDetail.parse(res.data);
				store(record);
				invalidate();
				options.onSuccess?.(record);
				return record;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

/*
 * Cache management
 */

export const useStoreAward = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(record: Award | AwardDetail) => {
			queryClient.setQueryData<Award | AwardDetail>(awardQueryKey(record.id), (existing) => {
				// Preserve detail-only fields (like approvalLevels) when mutation responses
				// return the base Award shape.
				if (!existing) return record;
				return { ...existing, ...record };
			});
		},
		[queryClient]
	);
};

/**
 * Invalidates awards caches across both admin and user-facing systems.
 *
 * This hook invalidates:
 * 1. Admin awards cache (this query system) - used in admin/programs/awards
 * 2. User-facing awards cache (bonus-feed query system) - used in give box
 *
 * When an admin creates/updates/deletes an award, users need to see the changes
 * immediately in their give box without refreshing the page.
 */
export const useInvalidateAwards = () => {
	const client = useQueryClient();
	const currentUser = useCurrentUser();

	return useCallback(() => {
		// Invalidate admin awards lists (admin interface)
		client.invalidateQueries(awardsQueryKey());

		// Invalidate user-facing awards (give box in recognition flow)
		// This targets the cache used by src/modules/bonus-feed/.../useGiveableAwards
		const giveableAwardsParams = {
			params: {
				type: AwardType.Manual,
				givable_by_user: currentUser.id,
			},
		};
		client.invalidateQueries([QueryKey.UserGiveableAwards, giveableAwardsParams]);
	}, [client, currentUser.id]);
};
